import {
  HeaderTabs,
  LoadingProgress,
  type HeaderTab,
} from '@issuemanager/apps/client/components';
import {
  APP_ERROR_TYPE,
  AttachmentsProvider,
  ERROR_USER_INTERFACE,
  useAppErrorHandler,
  useUserContext,
} from '@issuemanager/apps/client/contexts';
import { paths } from '@issuemanager/apps/client/paths';
import { useIssue } from '@issuemanager/client/features/issues';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { IssueStatusEnum } from '@issuemanager/constants';
import { Stack } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import DiscardIssueDialog from './components/discard-issue-dialog';
import { useIssueRelationships } from './hooks/use-issue-relationships';
import useIssueAuthorization from './hooks/useIssueAuthorization';

export default function IssueLayout(): React.JSX.Element | null {
  const { code } = useParams();
  const { t } = useTranslation(['common', 'issue']);
  const appErrorHandler = useAppErrorHandler();
  const { data: meData } = useUserContext();

  const [showDiscardIssueDialog, setShowDiscardIssueDialog] = useState(false);

  const issue = useIssue(code);
  const isIssueRelationshipsEnabled = useFeatureFlags('issueRelationships');
  const relationships = useIssueRelationships(code);

  const {
    issues: {
      index: issueIndex,
      manage: issueManage,
      relationships: issueRelationships,
    },
  } = paths;

  const { userRoles, canManage } = useIssueAuthorization(code);

  const tabs: HeaderTab[] = [
    { path: `/${issueIndex}/${code}`, label: t('issue:tabs.viewIssue') },
  ];

  if (canManage) {
    tabs.push({
      path: `/${issueIndex}/${code}/${issueManage}`,
      label: t('issue:tabs.manageIssue'),
    });
  }

  if (isIssueRelationshipsEnabled) {
    tabs.push({
      path: `/${issueIndex}/${code}/${issueRelationships}`,
      label: t('issue:tabs.relationships'),
      count: relationships.data?.length ?? 0,
    });
  }

  const showDiscardIssueBtn = useMemo(() => {
    const issueData = issue.data;
    const issueUserRolesData = userRoles.data;
    if (!issueData || !meData || !issueUserRolesData) return false;

    const isIssueManager = meData.id === issueData.manager?.id;
    const isIssueTypeManager = Boolean(issueUserRolesData.isIssueTypeManager);

    const hasNoIssueManager = !issueData.manager;
    const isNotClosedNorDiscarded =
      issueData.status !== IssueStatusEnum.CLOSED &&
      issueData.status !== IssueStatusEnum.DISCARDED;
    const canDiscardAsIssueManager = isIssueManager && isNotClosedNorDiscarded;
    const canDiscardAsIssueTypeManager =
      !!isIssueTypeManager && hasNoIssueManager && isNotClosedNorDiscarded;
    return canDiscardAsIssueManager || canDiscardAsIssueTypeManager;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.dataUpdatedAt, meData?.updatedAt, userRoles.dataUpdatedAt]);

  const isIssueNotFoundError = useMemo(
    () =>
      issue?.error?.response?.data?.type
        .toLowerCase()
        .includes(APP_ERROR_TYPE.ISSUE_NOT_FOUND.toLocaleLowerCase()),
    [issue?.error?.response?.data?.type],
  );

  if (isIssueNotFoundError) {
    appErrorHandler.setError({
      type: APP_ERROR_TYPE.ISSUE_NOT_FOUND,
      userInterface: ERROR_USER_INTERFACE.PAGE,
    });
  }

  if (!code) {
    appErrorHandler.setError({ type: APP_ERROR_TYPE.FORBIDDEN });
  }

  if (!code || isIssueNotFoundError) {
    return null;
  }

  return (
    <Stack height='100%' component='div'>
      {userRoles.isLoading || issue.isFetching ? (
        <LoadingProgress height={600} />
      ) : (
        <AttachmentsProvider issueCode={code}>
          <HeaderTabs tabs={tabs} />
          <Outlet
            context={{
              code,
              issue,
              userRoles,
              canDiscardIssue: showDiscardIssueBtn,
              onDiscardIssue: () => setShowDiscardIssueDialog(true),
              relationships: relationships.data ?? [],
            }}
          />
          <DiscardIssueDialog
            code={code}
            open={showDiscardIssueDialog}
            onClose={() => setShowDiscardIssueDialog(false)}
          />
        </AttachmentsProvider>
      )}
    </Stack>
  );
}
